import React, {PureComponent, Fragment, useRef, forwardRef} from 'react';
import PropTypes from 'prop-types';
import colors from "../../../util/colors";
import Image from "../elements/Image";
import log from "../../../util/log";
import _ from "lodash";
import c from "../../../util/const";
import {DragPreviewImage, DragSource, DropTarget, useDrag, useDrop} from 'react-dnd'
import {getEmptyImage} from "react-dnd-html5-backend";

class PdfThumbnail extends PureComponent {

  onPageDeleteClick(evt){
    evt.preventDefault();
    evt.stopPropagation();
    this.props.onDeletePageClick(this.props.pageIndex);
  }

  renderPageDeleteButton(){
    let {
      isEditModeOn,
    } = this.props;

    if(!isEditModeOn){
      return null;
    }

    return (
      <button className="btn btn-dark light-color position-absolute"
              onClick={this.onPageDeleteClick.bind(this)}
              style={{
                top: '-7px',
                right: '-7px',
                width: '34px',
                height: '34px',
                borderRadius : '50%',
                padding: '6px',
                border: '1px solid ' + colors.LIGHT
              }}>
        <i className="icon ion-close d-inline-flex"
           style={{
             fontSize: '20px',
             height: '20px',
             display: 'unset'
           }}/>
      </button>
    )
  }

  render(){
    let {
      pageIndex,
      canvasRef,
      thumbnailImage,
      dragImage,
      isActivePage,
      isEditModeOn,
      draggable,
      droppable,
      forwardedRef
    } = this.props;

    let {
      isDragging,
      drag,
      dragPreviewRef
    } = draggable;

    let {
      drop
    } = droppable;

    return (
        <div id={`pdf-thumbnail-page-${pageIndex}`}
             ref={forwardedRef}
             onClick={() => this.props.onThumbnailPageClick(pageIndex)}
             style={{marginBottom: '20px'}}
             className={
                 `${isDragging ? 'opacity-0 ' : ''}` +
                 `${isActivePage ? 'active ' : ''}` +
                 `${isEditModeOn ? 'is-editing has-grabber ' : 'has-pointer '}` +
                 'pdf-thumbnail text-center position-relative'
             }>
          <div>
            {this.renderPageDeleteButton()}
            <DragPreviewImage connect={dragPreviewRef} src={dragImage ? dragImage.data : getEmptyImage()}/>
            {thumbnailImage &&
                <Image src={thumbnailImage.data}
                       alt="thumb"
                       className={'no-pointer'}
                       imgHeight={thumbnailImage.height}
                       imgWidth={thumbnailImage.width}/>
            }
            {!thumbnailImage && canvasRef.current &&
                <div style={{
                  display: 'inline-block',
                  width: canvasRef.current.style.width,
                  height: canvasRef.current.style.height,
                  backgroundColor: colors.LIGHT
                }}/>
            }
          </div>
        </div>
    )
  }
}

PdfThumbnail.propTypes = {
  pageIndex : PropTypes.number.isRequired,
  listIndex : PropTypes.number.isRequired,
  canvasRef : PropTypes.object,
  thumbnailImage : PropTypes.object,
  dragImage : PropTypes.object,
  isEditModeOn : PropTypes.bool.isRequired,
  isActivePage : PropTypes.bool.isRequired,
  onThumbnailPageClick : PropTypes.func.isRequired,
  onDeletePageClick : PropTypes.func.isRequired,
  dragThumbnailStart : PropTypes.func.isRequired,
  dragThumbnailEnd : PropTypes.func.isRequired,
  onDragCancel : PropTypes.func.isRequired,
  doHoverMove : PropTypes.func.isRequired,
  commitMove : PropTypes.func.isRequired,
  disableHoverEvents : PropTypes.bool.isRequired
}

export default draggableComponent(droppableComponent(PdfThumbnail));

function droppableComponent(WrappedComponent) {
  const ComponentWithDroppableProp = forwardRef((props, ref) => {
    const [collectedProps, drop] = useDrop(() => ({
      accept : c.DRAG_DROP_TYPES.PDF_THUMBNAIL,
      hover: (item, monitor) => {
        if (props.disableHoverEvents) {
          return
        }
        const dragIndex = monitor.getItem().pageIndex;
        const hoverIndex = props.listIndex;
        // Don't replace items with themselves
        if (dragIndex === hoverIndex) {
          return;
        }

        //maybe this could be optimized?  We're querying a lot.
        const lookupID = `#pdf-thumbnail-page-${hoverIndex}`;
        let queried = document.querySelector(lookupID)
        const hoverBoundingRect = queried.getBoundingClientRect()

        // Get vertical middle
        const hoverMiddleY =
            (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
        // Determine mouse positionx
        const clientOffset = monitor.getClientOffset();
        // Get pixels to the top
        const hoverClientY = clientOffset.y - hoverBoundingRect.top
        // Only perform the move when the mouse has crossed half of the items height
        // When dragging downwards, only move when the cursor is below 50%
        // When dragging upwards, only move when the cursor is above 50%
        // Dragging downwards
        if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
          return
        }
        // Dragging upwards
        if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
          return
        }

        // Time to actually perform the action
        props.doHoverMove(dragIndex, hoverIndex);
        // Note: we're mutating the monitor item here!
        // Generally it's better to avoid mutations,
        // but it's good here for the sake of performance
        // to avoid expensive index searches.
        monitor.getItem().pageIndex = hoverIndex;
      },
      drop: (item, monitor) => {
        const dragIndex = monitor.getItem().pageIndex;
        const hoverIndex = props.pageIndex;

        log.log('pdf thumbnail drop', dragIndex, hoverIndex);

        if(dragIndex !== hoverIndex){
          props.commitMove(hoverIndex, dragIndex);
        }
        else{
          props.onDragCancel(hoverIndex);
        }
      }
    }))

    return (
      <WrappedComponent
        {...props}
        forwardedRef={(node) => {
          // log.log('pdf thumbnail dropForwardedRef', node);
          drop(node);
          if (props.forwardedRef) {
            props.forwardedRef(node);
          }
          if (ref) {
            if (typeof ref === 'function') ref(node);
            else ref.current = node;
          }
        }}
        droppable={{
          drop
        }}/>
    );
  })

  return ComponentWithDroppableProp;
}

function draggableComponent(WrappedComponent) {
  const ComponentWithDraggableProp = forwardRef((props, ref) => {

    const domRef = useRef(null);
    const [collected, drag, dragPreview] = useDrag(() => ({
      type: c.DRAG_DROP_TYPES.PDF_THUMBNAIL,
      item: () => {
        if (domRef.current) {
          props.dragThumbnailStart(domRef.current);
        }
        return {
          _originalPageIndex: props.pageIndex,
          pageIndex: props.pageIndex
        }
      },
      end: (item, monitor) => {
        props.dragThumbnailEnd();
        if (!monitor.didDrop()) {
          props.onDragCancel();
          return;
        }
      },
      options: { dropEffect: 'move' },
      collect: (monitor) => ({
        isDragging : monitor.isDragging()
      }),
      canDrag: (monitor) => {
        return props.isEditModeOn;
      }
    }), [])

    return (
      <WrappedComponent
        {...props}
        forwardedRef={(node) => {
          // log.log('pdf thumbnail dragForwardedRef', node, props);
          domRef.current = node; // For your measurements
          drag(node);            // For React-DnD
          if (ref) {
            if (typeof ref === 'function') ref(node);
            else ref.current = node;
          }
        }}
        draggable={{
          drag,
          dragPreviewRef: dragPreview,
          isDragging: collected.isDragging,
        }}/>
    );
  })
  return ComponentWithDraggableProp;
}